// Step-by-step "how to build an estimate" guide for leadership (George
// 2026-07-01). Estimating is admin/leadership-only, so cost/margin detail is
// fine here — do NOT reuse this content anywhere reps can see. Self-contained:
// renders the 📖 How-To Guide button + its modal. Content mirrors
// docs/ESTIMATING-GUIDE.md — update both together.
import React, { useState } from 'react';
import { styles } from './UI';

const ORANGE = '#E36F2C';

function Step({ n, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
      <div style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
        background: '#1F2937', color: '#fff', fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );
}

function Part({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 800, color: '#fff', background: '#1F2937',
      borderRadius: 4, padding: '5px 12px', margin: '18px 0 12px 0',
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{children}</div>
  );
}

const li = { marginBottom: 3 };
const ul = { margin: '4px 0 0 0', paddingLeft: 18 };

export default function EstimatingGuide() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button style={{
          ...styles.btnSecondary, whiteSpace: 'nowrap',
          border: `2px solid ${ORANGE}`, color: ORANGE, background: '#FFF7ED',
          fontWeight: 700, boxShadow: '0 1px 3px rgba(227,111,44,0.3)',
        }}
        title="Step-by-step: request → estimate → approval → quote → revisions"
        onClick={() => setOpen(true)}>
        📖 How-To Guide
      </button>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 8, width: 820, maxWidth: '95vw',
            maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>
                  How to Build an Estimate — Start to Finish
                </div>
                <div style={{ fontSize: 11.5, color: '#6B7280' }}>
                  Request → estimate → approval → quote → revisions. Leadership only — this covers cost and margin.
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{
                border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280',
              }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto' }}>

              <Part>Part 1 — Build the estimate</Part>

              <Step n={0} title="The status board">
                This page opens on a row of count cards. <strong>⏳ New requests</strong> counts submitted sales
                requests nobody has priced yet (amber when work is waiting, gray at zero) and jumps you to the queue —
                it sits apart from the rest because a request isn't an estimate and never shows in the four status
                counts. <strong>All Estimates / Draft / Submitted / Approved / Rejected</strong> filter the list:
                click a card to show only that status, click again to clear. The counts follow the name, date, and
                salesperson filters but ignore the status filter, so the numbers stay put while you click through
                them. The board is on the list view only — it clears once you open an estimate.
              </Step>

              <Step n={1} title="Start from the request (or standalone)">
                Submitted requests land in <strong>⏳ Waiting for Estimate</strong> on this page (and in the Requests
                Queue). Click <strong>📊 Create Estimate</strong> — it clones the request's labor, equipment, and
                consumable lines with the AM's target rates as your starting numbers, links the MSA sheet, and ties
                everything to the opportunity. Use <strong>+ New Estimate</strong> (pick a project first) only for
                deals with no request. If the deal isn't in the Project Tracker at all, pick
                <strong> ➕ Standalone — no opportunity yet</strong> in that same dropdown: name the deal and CRM
                creates the Bidding opportunity for you, then opens the estimate on it. Just want a peek first?
                <strong> 👁 Review Request</strong> on the same row opens the request read-only and creates nothing. Open
                <strong> 📄 View Request</strong> any time to read the questionnaire and the AM's answers side by side.
                No request behind the estimate? <strong>📨 Request from Sales</strong> (same spot) lets you check the
                scope options and email the salesperson a Draft request already linked to this estimate.
                <div style={{ marginTop: 8 }}>
                  <strong>Looking at a request doesn't claim it.</strong> Creating the estimate is how you read how
                  the job was scoped, so it leaves the request in <strong>⏳ Waiting for Estimate</strong> — the
                  button then reads <strong>📊 Open Estimate</strong> and the list row reads <em>Not started</em>.
                  The request only leaves the queue (status <strong>Estimated</strong>, your name on it) when someone
                  works it: the first edit — a rate, a line, a header change — claims it. Opened one by mistake?
                  Delete the Draft estimate (🗑 on its list row) and the request drops straight back into the queue.
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Leave the Account Manager blank on a standalone</strong> and the opportunity shows in every
                  AM's Opportunity dropdown on the Requests page marked <strong>· unassigned</strong>. When an AM
                  links their request to it, the opportunity is claimed into their tracker and the questionnaire,
                  files, and MSA sheet attach to the chain your estimate is already on — price first, let sales
                  document it afterwards. Set an AM instead and only that AM sees it.
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Deleting a Draft estimate</strong> asks what to do with the opportunity. Nothing else
                  attached (no other estimates, requests, quotes, activity, files, crew, job number, PO, or billed
                  value, and still Bidding) → a second prompt offers to delete both. Otherwise the first prompt names
                  what is keeping it and the opportunity stays. Delete is still Draft-only — withdraw a Submitted
                  estimate first.
                </div>
              </Step>

              <Step n={2} title="Set the header — the pricing frame">
                <ul style={ul}>
                  <li style={li}><strong>Util. Tier (50 / 70 / 100%)</strong> — the utilization scenario used for
                    metered equipment and the Summary totals. The quote shows all three; the tier picks which one
                    you're pricing to.</li>
                  <li style={li}><strong>OT Rule</strong> — default weekly-over-40; change only if the site's
                    agreement says otherwise.</li>
                  <li style={li}><strong>Day Rate</strong> — tick "Quote as day rate" when the customer wants one
                    $/day (Rescue). Build the estimate exactly as normal; the orange Day Rate panel then shows the rate
                    line by line: each crew line at heads × (8 ST + the balance of the shift OT), per diem × positions,
                    and each equipment line at qty × day rate (metered gear at the tier). Labor ignores the OT Rule on
                    purpose, so the rate is the same for a 3-day or a 10-day job. Rate-only crafts, hourly or rate-less
                    equipment, consumables and mobilization are listed under "Not in the day rate" and print on the
                    quote as charges billed in addition. The quote replaces the labor table and the in-rate equipment
                    with one DAY RATE line and drops the job total.</li>
                  <li style={li}><strong>Margin % (ST)</strong> — target margin grading ST labor and every non-labor
                    category (default 0.20). <strong>Margin % (OT)</strong> grades OT labor only (default 0.15 —
                    looser because the OT premium dilutes margin).</li>
                  <li style={li}><strong>OT Burden %</strong> — derived automatically from the markup components
                    flagged "OT" (FICA + unemployment, the burdens that legally follow premium dollars). Gray = derived;
                    change it by toggling the OT chip on a component, not by typing.</li>
                  <li style={li}><strong>Markup panel</strong> — the burden profile (FICA, unemployment, WC, liability,
                    overhead, profit lines). Check what applies; every labor cost calc runs off it. Set <strong>Net
                    Profit / Fee</strong> so the All-in % lands on the target for the job type: TA / equipment-heavy 50%,
                    labor only 70%, Rescue 80-100% (guidance printed beside the field).</li>
                </ul>
              </Step>

              <Step n={3} title="Labor tab — price the hours">
                <ul style={ul}>
                  <li style={li}>Verify each craft and its <strong>MSA Description</strong> (the exact contract wording —
                    it's what prints on the quote when set).</li>
                  <li style={li}><strong>Pay ST/OT</strong> = wages we pay (Pay OT auto-locks to 1.5× Pay ST except
                    Subcontractor lines). <strong>Bill ST/OT</strong> = customer rates — Bill OT is always entered
                    manually. <strong>Subcontractor lines:</strong> Pay ST is what the sub charges us; Pay OT seeds at
                    1.5× ST and follows it until you type your own. Type the <strong>ST Markup %</strong> in the Markup
                    column and Bill ST AND Bill OT are set to sub cost × (1 + markup), and Margin re-derives. The
                    <strong>OT %</strong> box beneath gives OT its own markup (Bill OT only). Changing a sub cost later
                    holds its markup and re-marks-up that bill; typing a bill rate by hand breaks the link.</li>
                  <li style={li}><strong>Phases</strong> — dates + day/night headcount + hours/day + work-day pattern
                    (all / M–F / M–Th / M–Sat / 13 on 1 off / 13 on 1 off staggered) per phase. Staggered rests each
                    person on their own day (last week of each 14, PM on day 14, Night Super on day 13), never more than
                    13 in a row. Phases never overlap. This drives every hour, per-diem day,
                    and the Gantt exports. Never leave hours/day blank.</li>
                  <li style={li}><strong>Margin/Markup columns are per-hour, ST and OT separately</strong>, excluding
                    per diem and mobilization. Orange = below its threshold. <strong>$/Hr ST after burden</strong>
                    sanity-checks a rate before hours exist.</li>
                  <li style={li}><strong>Section</strong> (Turnaround / Safety / CSVM / Rescue) on each line groups the
                    Billed Summary and Org Chart — set it, don't rely on the craft-name fallback.</li>
                  <li style={li}><strong>Rate-only</strong> lines print a rate on the quote with zero hours. Per-diem
                    <strong> travel days</strong> default 2.</li>
                </ul>
              </Step>

              <Step n={4} title="Equipment tab — owned gear">
                <strong>Type</strong> drives billing: <em>Utilization</em> meters at the tier scenarios;
                <em> Non-Consignment</em> bills 100% (prints as "Rentals" to the customer). Set qty +
                <strong> Start/End dates</strong> (Days auto-fills; a dateless line spans the whole labor window).
                <strong> Dup</strong> clones a line for qty ramps; <strong>⤓ all</strong> copies one line's dates
                everywhere; <strong>Phases</strong> on a line ramps qty by window (one day-rate per line). Watch for
                amber <strong>no CAT-CLASS</strong> warnings — those lines can't match Rental Man stock downstream.
              </Step>

              <Step n={5} title="Third-Party Rentals tab — re-rented gear">
                Lines with Type = Third-Party live here. Enter <strong>vendor name/contact</strong> and the vendor's
                <strong> day/week/month rates</strong>; <strong>Bill mode</strong> "best rate" runs the cheapest
                ladder over the span, "calendar" is day-rate × every day. <strong>Bill basis</strong>: straight
                (100%, folds into Rentals on the quote) or utilization (rides the tier scenarios). Upload the
                <strong> vendor quote file</strong> on the line — it stays through revisions. The customer never
                sees "Third-Party".
              </Step>

              <Step n={6} title="Consumables, Services and Charges tab">
                Pick the right <strong>Category</strong> per line (Consumable / Service / Charge / Mobilization /
                Air Sale) — each gets its own Summary row. Enter cost; <strong>Markup %</strong> is over cost
                (cost × (1+m) = bill). Typing the first cost on a line that already has a Bill Rate keeps
                that Bill Rate and fills Markup % from the two. The header <strong>Default markup %</strong> + <strong>Apply to all lines</strong>
                fixes a whole tab in one click. Editing Bill Rate directly is a manual override.
              </Step>

              <Step n={7} title="Personnel tab — name the crew (optional at bid, required at submittal)">
                Slots are seeded from each craft's peak day/night headcount. Fill <strong>Name / Email / Phone</strong> —
                names suggest from the Operations roster and write back to it on save. Exports:
                <ul style={ul}>
                  <li style={li}><strong>⬇ Personnel Gantt</strong> (.xlsx, 3 sheets — see the Gantt step below).</li>
                  <li style={li}><strong>⬇ Org Chart</strong> (PDF) — PM apex → leads with email/phone → crew lists.</li>
                </ul>
              </Step>

              <Step n={8} title="Summary tab — the go/no-go screen">
                Read it top to bottom before submitting:
                <ul style={ul}>
                  <li style={li}><strong>Deal Economics</strong> — man-hours, GP per man-hour, and the
                    <strong> Self-Perform vs Pass-Through</strong> split. A healthy self-perform margin can hide
                    behind a pass-through-diluted blended number — this is where you see both.</li>
                  <li style={li}><strong>Category table</strong> — revenue / cost / GP / margin / markup per category
                    with BELOW flags against the thresholds. The tab shows ⚠ when anything is below.</li>
                  <li style={li}><strong>Three tier cards</strong> — the 50/70/100 totals the customer will see.</li>
                </ul>
              </Step>

              <Step n={9} title="Excel round-trip and the Billed Summary">
                <ul style={ul}>
                  <li style={li}><strong>⬇ Workbook / ⬆ Import</strong> — edit Labor, Equipment, Third-Party, and
                    Consumables in Excel and import back. Import previews every add/change/delete before writing.
                    Never touch the hidden <strong>line_id</strong> column: blank = new line, deleted row = deleted line.</li>
                  <li style={li}><strong>📊 Billed Summary</strong> — the customer-safe consolidated workbook
                    (Dashboard, Manpower, Equipment, Gantt, Org Chart). Bill-side only, no cost or margin —
                    this is the submittal package.</li>
                </ul>
              </Step>

              <Step n={10} title="The Gantt charts and what each is for">
                <ul style={ul}>
                  <li style={li}><strong>Billed Summary → Gantt sheet</strong> — the day-by-day job chart: one bar
                    per craft showing shift hours each day with (×N) headcount, equipment bars showing qty on site
                    per day, and daily totals (headcount / ST / OT / per diem). Use it to sanity-check the schedule
                    and send it to the customer with the submittal.</li>
                  <li style={li}><strong>Personnel Gantt sheet</strong> — the same timeline with the NAMED crew, one
                    row per person (hours per on-site day + Total Hrs). Reconciles exactly with the priced hours.</li>
                  <li style={li}><strong>Fatigue sheet</strong> — the 13-on / 1-off rotation plan: red OFF marks each
                    person's rest days, staggered so only one person per shift is ever off, supervision covering
                    weekends. Customers with fatigue policies ask for exactly this.</li>
                  <li style={li}><strong>Operations → Equipment Gantt page</strong> — cross-job demand planning. Flip
                    the opportunity onto the Gantt and you get: equipment demand vs owned Rental Man stock,
                    availability on a date / full-timeline shortage view, manpower demand curves, and the
                    <strong> Allocation Worksheet</strong> (build the per-branch pull plan, fill from 800, flag the
                    3rd-party rental gap, email pull/transfer drafts per branch, export CSV, save the plan).</li>
                </ul>
              </Step>

              <Part>Part 2 — Approval, quote, and out the door</Part>

              <Step n={11} title="Submit for approval">
                Set <strong>Status → Submitted</strong>. That emails the full Summary (economics, category table,
                tiers — cost and GP included) to the approval chain, auto-drafts the quote, and puts the estimate in
                the blue <strong>✋ Waiting for Approval</strong> strip. <strong>↩ Withdraw</strong> pulls it back to
                Draft. The approver opens the Summary tab and sets <strong>Approved</strong> or
                <strong> Rejected</strong> (notes on the estimate).
              </Step>

              <Step n={12} title="What Approval does automatically">
                Pricing locks (revise for changes). The bill-side total writes to the opportunity's
                <strong> estimated value</strong> (feeds the Forecast KPI), the quote PDF attaches to the
                opportunity, and Operations → Allocations populates: craft headcounts become the staffing plan
                and the <strong>named people on the Personnel tab are assigned to the job automatically</strong>
                (names saved to Personnel after approval flow across too). The green banner's
                <strong> ⬆ Push to Opportunity</strong> re-runs those pushes for older estimates;
                <strong> 👷 Resync Crew Plan</strong> re-seeds the plan + assignments after changes — it only adds,
                never overwrites what ops already manages.
              </Step>

              <Step n={13} title="Quote tab — issue and email to Sales">
                One quote chain per estimate chain. <strong>+ Create Quote</strong> works at any estimate status —
                but until the estimate is Approved the PDF carries an amber <strong>DRAFT — pricing not yet
                approved</strong> banner (preview only, never for customer issue).
                <ul style={ul}>
                  <li style={li}><strong>🖨 Print / PDF</strong> — the customer document: bill rates only, MSA
                    descriptions when set, three utilization tiers, never cost or margin.</li>
                  <li style={li}><strong>📧 Email to Sales</strong> — emails the PDF to the <strong>account manager who
                    owns the deal</strong> (a confirm dialog names the recipient first — read it). The AM forwards it
                    to their customer. <strong>Quotes never go directly to the customer from CRM.</strong></li>
                  <li style={li}><strong>Status</strong> — Draft → Sent (Sent also emails the AM automatically) →
                    Accepted / Declined as the customer responds.</li>
                </ul>
              </Step>

              <Step n={14} title="Equipment pushes to Inventory">
                <strong>📦 Reserve in Inventory</strong> — a soft hold on this estimate's equipment for warehouse
                planning (works at any status; keyed to the estimate; promotes automatically when the job is won).
                <strong> 📥 Push Initial Pull List</strong> — a REAL pullable list for a verbally-won bid (needs a job
                number on the opportunity). Re-pushes update the same list and never erase already-pulled barcodes.
              </Step>

              <Part>Part 3 — Revisions</Part>

              <Step n={15} title="When sales changes the request after you've started">
                A red <strong>change-order banner</strong> appears on the estimate: "Sales submitted REQ-x-n after
                this estimate." Click <strong>📄 View REQ</strong> to read what changed, then
                <strong> Build estimate revision →</strong> — it clones your estimate into -1/-2 linked to the new
                request revision. Sales is never hard-blocked from revising; the banner + History are your paper
                trail, so nothing changes verbally.
              </Step>

              <Step n={16} title="Estimate revision → finish">
                <strong>📝 New Revision</strong> (or the banner button) clones everything — lines, phases, markup,
                personnel, vendor-quote links; the old revision freezes as the audit record. Rework the numbers →
                Status → Submitted → Approved again. Approval re-pushes the new value + PDF to the opportunity.
              </Step>

              <Step n={17} title="Quote revision → back to Sales">
                On the Quote tab click <strong>📝 Revise</strong> — the new quote revision (-1/-2) prices from the
                chain's <strong>current Approved estimate</strong>, and the superseded quote freezes. Then
                <strong> 📧 Email to Sales</strong> again so the AM has the new PDF to forward. Every revision's PDF
                also lands on the opportunity, newest alongside the old — the full history stays visible.
              </Step>

              <Step n={18} title="Disputes — the audit trail">
                <strong>🧾 Audit Trail</strong> merges the whole chain (request → estimate → quote → PO) into one
                chronological log — who changed what, when, on which revision — exportable to PDF. Use it whenever
                "I never said that" comes up.
              </Step>

              <div style={{
                background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 6,
                padding: '10px 14px', fontSize: 12.5, color: '#92400E', marginTop: 4,
              }}>
                <strong>Bottom line:</strong> the Summary tab is the gate — don't submit with unexplained BELOW flags.
                Quotes only leave through Sales, never straight to the customer. And every change after approval is a
                revision, never an edit — the frozen chain is what protects you in a dispute.
              </div>
            </div>
            <div style={{ padding: '10px 20px', borderTop: '1px solid #E5E7EB', textAlign: 'right' }}>
              <button style={{ ...styles.btn, background: ORANGE }} onClick={() => setOpen(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
